import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { Form, Button, ButtonGroup, FormControl, InputGroup, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdvancedMarker, APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer, MarkerUtils } from "@googlemaps/markerclusterer";
import Draggable from "react-draggable";
import { Resizable } from "re-resizable";
import Icon from "@mdi/react";
import {
	mdiAirplane,
	mdiAlphaHCircleOutline,
	mdiArrowCollapseHorizontal,
	mdiArrowExpandHorizontal,
	mdiChevronRight,
	mdiClock,
	mdiClose,
	mdiFullscreen,
	mdiMapMarkerDistance,
	mdiMapSearchOutline,
	mdiMenu,
	mdiPin,
	mdiViewGridPlusOutline,
} from "@mdi/js";

import ApiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import { StatusVooMapaModel } from "../../models/StatusVooModel";
import { MissaoLogDetalhesModel } from "../../models/MissaoLogModel";

import Layout from "../../components/Layout";
import VideoJS from "../../components/VideoJS";
import { calcularZoom } from "../../utils/Map";

export default function DashboardMapa() {
	const { user, cliente, handleLogout } = useAuth();
	const apiService = new ApiService(handleLogout);
	const [searchParams, setSearchParams] = useSearchParams();

	const containerRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState<number | null>(null);
	const [center, setCenter] = useState<{ lat: number; lng: number }>();
	const [zoom, setZoom] = useState(12);
	const [formBounds, setFormBounds] = useState<MapBoundsFilter | undefined>(
		searchParams.get("north")
			? {
					center: {
						lat: Number(searchParams.get("lat")),
						lng: Number(searchParams.get("lng")),
					},
					zoom: Number(searchParams.get("zoom")),
					north: Number(searchParams.get("north")),
					east: Number(searchParams.get("east")),
					south: Number(searchParams.get("south")),
					west: Number(searchParams.get("west")),
			  }
			: undefined
	);
	const onBoundsChangeTimeout = useRef<NodeJS.Timeout | null>(null);

	const [videos, setVideos] = useState<MissaoLogDetalhesModel[]>([]);
	const [enderecoLoading, setEnderecoLoading] = useState(false);
	const [grid, setGrid] = useState(searchParams.get("grid") ?? "0");
	const [list, setList] = useState(searchParams.get("list") === "1");
	const [listExpand, setListExpand] = useState(searchParams.get("listExpand") === "1");
	const [listData, setListData] = useState<StatusVooMapaModel[]>([]);

	const documentFull = document as any;
	const documentFullSupport = documentFull.fullscreenEnabled || documentFull.webkitFullscreenEnabled || documentFull.msFullscreenEnabled;

	const queryHomepoint = useQuery(["homepoint", cliente!.id], () => fetchDataHomepoint(), { refetchOnWindowFocus: false });
	const queryVoosMapa = useQuery(["voosMapa", cliente!.id, formBounds], () => fetchDataVoosMapa(), {
		enabled: !!queryHomepoint.data && !!formBounds,
		refetchOnWindowFocus: false,
		refetchInterval: 60000,
		keepPreviousData: true,
	});

	useEffect(() => {
		if (containerRef.current) {
			const width = containerRef.current.offsetWidth;
			setContainerWidth(width);
		}
	}, []);

	useEffect(() => {
		function handleFullscreenChange() {
			if (documentFull.fullscreenElement || documentFull.webkitFullscreenElement || documentFull.mozFullScreenElement || documentFull.msFullscreenElement) {
				containerRef.current!.style.setProperty("position", "fixed", "important");
			} else {
				containerRef.current!.style.setProperty("position", "relative");
			}
		}

		document.addEventListener("fullscreenchange", handleFullscreenChange);
		document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
		document.addEventListener("mozfullscreenchange", handleFullscreenChange);
		document.addEventListener("MSFullscreenChange", handleFullscreenChange);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
			document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
			document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
			document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
		};
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (formBounds) {
			setSearchParams({
				lat: formBounds.center.lat.toString(),
				lng: formBounds.center.lng.toString(),
				zoom: formBounds.zoom.toString(),
				north: formBounds.north.toString(),
				east: formBounds.east.toString(),
				south: formBounds.south.toString(),
				west: formBounds.west.toString(),
				list: list ? "1" : "0",
				listExpand: listExpand ? "1" : "0",
				grid: grid,
			});
		}
		// eslint-disable-next-line
	}, [formBounds, list, listExpand, grid]);

	async function fetchDataHomepoint() {
		let respHome = await apiService.getHome(cliente!.id);
		let respSOS = await apiService.getSOS(cliente!.id);
		if (respHome.Result !== 1) {
			if (user?.parametros) {
				return {
					...respHome,
					Data: {
						latitude: user.parametros.baseLatitude,
						longitude: user.parametros.baseLongitude,
					},
				};
			}
			return { ...respHome, Data: undefined };
		}
		if (respSOS.Result !== 1) {
			return { ...respSOS, Data: undefined };
		}
		return {
			Result: 1,
			Message: undefined,
			Data: { homepoint: respHome.Data, sos: respSOS.Data },
		};
	}

	async function fetchDataVoosMapa() {
		let resp = await apiService.GetStatusVooMapa(`${formBounds!.east}`, `${formBounds!.north}`, `${formBounds!.south}`, `${formBounds!.west}`);

		setListData(resp.Data ?? []);

		if (resp.Data && queryVoosMapa.data?.Data) {
			resp.Data = [...queryVoosMapa.data?.Data, ...resp.Data].filter((item, index, self) => index === self.findIndex((other) => other.vooId === item.vooId));
		}

		return resp;
	}

	function handleFullScreen() {
		if (documentFullSupport) {
			if (documentFull.fullscreenElement) {
				documentFull.exitFullscreen();
			} else if (documentFull.webkitFullscreenElement) {
				documentFull.webkitExitFullscreen();
			} else if (documentFull.msFullscreenElement) {
				documentFull.msExitFullscreen();
			} else {
				documentFull.body.requestFullscreen();
			}
		}
	}

	function handleList() {
		setList(!list);
	}

	function handleListExpand() {
		setListExpand(!listExpand);
	}

	async function handleClick(missaoLogId: number) {
		setVideos((prev) => [...prev, { missaoId: missaoLogId } as any]);
	}

	function handleGrid() {
		if (grid === "0") {
			setGrid("2");
		} else if (grid === "2") {
			setGrid("4");
		} else if (grid === "4") {
			setGrid("6");
		} else if (grid === "6") {
			setGrid("8");
		} else {
			setGrid("0");
		}
	}

	async function handleEndereco(endereco: string) {
		setEnderecoLoading(true);

		try {
			const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
			const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(endereco)}&key=${apiKey}`);
			const data = await response.json();
			if (data.status === "OK" && data.results.length > 0) {
				const { lat, lng } = data.results[0].geometry.location;
				const viewport = data.results[0].geometry.viewport;
				const center = { lat, lng };
				const zoom = calcularZoom({
					north: viewport.northeast.lat,
					east: viewport.northeast.lng,
					south: viewport.southwest.lat,
					west: viewport.southwest.lng,
				});
				if (formBounds) {
					setFormBounds({
						...formBounds,
						center: center,
						zoom: zoom,
						north: viewport.northeast.lat,
						east: viewport.northeast.lng,
						south: viewport.southwest.lat,
						west: viewport.southwest.lng,
					});
					setCenter(center);
					setZoom(zoom);
				}
			} else {
				alert("Endereço não encontrado!");
			}
		} catch (e) {
			console.error("Erro ao buscar endereço:", e);
			alert("Erro ao buscar endereço!");
		}

		setEnderecoLoading(false);
	}

	function handleMapBoundsChanged(detail: { center: google.maps.LatLngLiteral; bounds: google.maps.LatLngBoundsLiteral; zoom: number; heading: number; tilt: number }) {
		setCenter(detail.center);
		setZoom(detail.zoom);
		if (onBoundsChangeTimeout.current) clearTimeout(onBoundsChangeTimeout.current);
		onBoundsChangeTimeout.current = setTimeout(() => {
			setFormBounds({
				center: detail.center,
				zoom: detail.zoom,
				...detail.bounds,
			});
		}, 200);
	}

	return (
		<Layout>
			<div className={`d-flex position-relative`} style={{ width: "100%", height: "100%", zIndex: 10000, top: 0, left: 0 }} ref={containerRef}>
				<APIProvider apiKey={`${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}>
					{queryHomepoint.isFetching && <Spinner className="m-auto" />}
					{!queryHomepoint.isFetching && queryHomepoint.data?.Data?.homepoint && (
						<Map
							mapId={"dashboard"}
							defaultCenter={formBounds?.center ?? { lat: queryHomepoint.data.Data.homepoint?.latitude!, lng: queryHomepoint.data.Data.homepoint?.longitude! }}
							defaultZoom={formBounds?.zoom ?? 10}
							center={center}
							zoom={zoom}
							fullscreenControl={false}
							streetViewControl={false}
							zoomControlOptions={{ position: google.maps.ControlPosition.BOTTOM_LEFT }}
							mapTypeControlOptions={{ position: google.maps.ControlPosition.BOTTOM_CENTER, mapTypeIds: ["roadmap", "satellite"] }}
							gestureHandling={"greedy"}
							onBoundsChanged={(e) => {
								handleMapBoundsChanged(e.detail);
							}}
							style={{ width: "100%", height: "100%" }}
						>
							<DashboardMapaMarkers
								base={{ lat: queryHomepoint.data.Data.homepoint.latitude, lng: queryHomepoint.data.Data.homepoint.latitude }}
								sos={queryHomepoint.data.Data.sos ? { lat: queryHomepoint.data.Data.sos.latitude, lng: queryHomepoint.data.Data.sos.longitude } : undefined}
								markers={queryVoosMapa.data?.Data || []}
								videos={videos}
								onVideosChange={(videos) => {
									setVideos(videos);
								}}
								onClusterClick={() => {
									setList(true);
								}}
							/>
						</Map>
					)}
				</APIProvider>
				<div
					className={`position-absolute ${grid !== "0" ? "d-grid" : ""}`}
					style={{ top: 45, left: 5, right: 5, bottom: 20, gridTemplateColumns: grid !== "0" ? `repeat(${grid}, 1fr)` : undefined, gap: 4, alignContent: "start", pointerEvents: "none" }}
				>
					{videos.map((video, videoIndex) => {
						return (
							<DashboardMapaVideo
								key={videoIndex}
								width={Number(containerWidth)}
								index={videoIndex}
								data={undefined}
								missaoLogId={video.missaoId!}
								onClose={() => {
									setVideos((prev) => prev.filter((_, itemIndex) => itemIndex !== videoIndex));
								}}
								template={grid !== "0" ? "grid" : "absolute"}
							/>
						);
					})}
				</div>
				<Form
					onSubmit={(e) => {
						e.preventDefault();
						let endereco = e.currentTarget.endereco.value;
						if (endereco.length > 3) {
							handleEndereco(endereco);
						}
					}}
				>
					<InputGroup className="shadow-sm" style={{ position: "absolute", top: 5, left: 5, zIndex: 2, width: 300, height: 34 }}>
						<FormControl placeholder="Pesquisar endereço" name="endereco" size="sm" id="dashboardMapaEndereco" className="bg-air-dark text-white border-0" disabled={enderecoLoading} />
						<Button type="submit" variant="dark" onClick={handleGrid} size="sm" disabled={enderecoLoading}>
							<Icon path={mdiMapSearchOutline} size={1} />
						</Button>
					</InputGroup>
				</Form>
				<ButtonGroup className="shadow-sm" style={{ position: "absolute", top: 5, right: 5, zIndex: 2 }}>
					<Button variant="air-dark" onClick={handleGrid} size="sm">
						<Icon path={mdiViewGridPlusOutline} size={1} />
					</Button>
					<Button variant="air-dark" onClick={handleList} size="sm">
						<Icon path={mdiMenu} size={1} />
					</Button>
					<Button variant="air-dark" onClick={handleFullScreen} size="sm">
						<Icon path={mdiFullscreen} size={1} />
					</Button>
				</ButtonGroup>
				{list && (
					<div
						id="dashboardMapaList"
						className="d-flex flex-column text-white rounded shadow-sm"
						style={{
							position: "absolute",
							top: 45,
							right: 5,
							bottom: 60,
							zIndex: 2,
							width: listExpand ? "80%" : 300,
							overflowY: "auto",
							overflowX: "hidden",
							background: "rgba(31, 37, 60, 0.6)",
							backdropFilter: "blur(5px)",
						}}
					>
						<h5 className="p-2 pb-0 d-flex align-items-center fw-light flex-wrap">
							<Icon path={mdiAirplane} size={1} className="me-1" />
							Voos {queryVoosMapa.isFetching && <Spinner size="sm" className="ms-1" variant="secondary" />}
							<Button variant="air-dark" onClick={handleListExpand} size="sm" className="float-right ms-auto">
								<Icon path={listExpand ? mdiArrowCollapseHorizontal : mdiArrowExpandHorizontal} size={1} />
							</Button>
						</h5>
						{listData.map((item, index) => {
							return (
								<div
									key={index}
									role="button"
									className="d-flex align-items-center gap-1 border-bottom border-air-dark px-2 py-1 list-card"
									onClick={() => {
										handleClick(item.missaoLogId);
									}}
									style={{ cursor: "pointer" }}
								>
									<div style={{ width: "33%" }}>
										<div className={`text-nowrap  fw-light small`}>Piloto</div>
										<div className={`small line-1`} title={item.piloto}>
											{item.piloto ?? "..."}
										</div>
									</div>
									<div style={{ width: "33%" }}>
										<div className={`text-nowrap  fw-light small`}>Drone</div>
										<div className={`small line-1`} title={item.drone}>
											{item.drone ?? "..."}
										</div>
									</div>
									<div style={{ width: "33%" }}>
										<div className={`text-nowrap  fw-light small`}>Data</div>
										<div className={`small line-1`} title={item.data}>
											{item.data ?? "..."}
										</div>
									</div>
								</div>
							);
						})}
					</div>
				)}
				<div
					className="position-absolute d-flex justify-content-between text-white rounded shadow-sm p-2"
					style={{ right: 5, bottom: 10, zIndex: 2, width: 300, background: "rgba(31, 37, 60, 0.6)", backdropFilter: "blur(5px)" }}
				>
					<div>
						<Icon path={mdiAirplane} size={1} /> {listData.length}
					</div>
					{/* <div>
						<Icon path={mdiClock} size={1} /> 5:00
					</div>
					<div>
						<Icon path={mdiMapMarkerDistance} size={1} /> 5km
					</div> */}
				</div>
			</div>
		</Layout>
	);
}

type MapBoundsFilter = {
	center: { lat: number; lng: number };
	zoom: number;
	north: number;
	east: number;
	south: number;
	west: number;
};

type DashboardMapaMarkersProps = {
	base: { lat: number; lng: number };
	sos?: { lat: number; lng: number };
	markers: StatusVooMapaModel[];
	videos: MissaoLogDetalhesModel[];

	onVideosChange: (videos: MissaoLogDetalhesModel[]) => void;
	onClusterClick: () => void;
};

function DashboardMapaMarkers(props: DashboardMapaMarkersProps) {
	const map = useMap();
	const mapCluster = useMemo(() => {
		let markerCluster = new MarkerClusterer({
			map: map,
			algorithmOptions: { maxZoom: 30 },
			onClusterClick: (event, cluster, map) => {
				map.fitBounds(cluster.bounds!);
				props.onClusterClick();
			},
			renderer: {
				render({ count, position }, stats, map) {
					const color = count > Math.max(10, stats.clusters.markers.mean) ? "#1b93c8" : "#147199";
					const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
						<circle cx="120" cy="120" opacity=".6" r="70" />
						<circle cx="120" cy="120" opacity=".3" r="90" />
						<circle cx="120" cy="120" opacity=".2" r="110" />
						<text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
						</svg>`;
					const title = `Agrupamento de ${count} voos`;
					const zIndex = Number(google.maps.Marker.MAX_ZINDEX) + count;
					if (MarkerUtils.isAdvancedMarkerAvailable(map)) {
						const parser = new DOMParser();
						const svgEl = parser.parseFromString(svg, "image/svg+xml").documentElement;
						svgEl.setAttribute("transform", "translate(0 25)");
						const clusterOptions = {
							map,
							position,
							zIndex,
							title,
							content: svgEl,
						};
						return new google.maps.marker.AdvancedMarkerElement(clusterOptions);
					}
					const clusterOptions = {
						position,
						zIndex,
						title,
						icon: {
							url: `data:image/svg+xml;base64,${btoa(svg)}`,
							anchor: new google.maps.Point(25, 25),
						},
					};
					return new google.maps.Marker(clusterOptions);
				},
			},
		});

		return markerCluster;
		// eslint-disable-next-line
	}, []);
	const [markers, setMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([]);

	useEffect(() => {
		if (map && mapCluster && markers) {
			mapCluster.clearMarkers();
			mapCluster.addMarkers(Object.values(markers));
		}
	}, [map, mapCluster, markers]);

	return (
		<>
			<DashboardMapaMarker position={{ lat: props.base.lat, lng: props.base.lng }}>
				<Icon path={mdiAlphaHCircleOutline} size={1.5} className="text-danger bg-white rounded-circle" style={{ filter: "drop-shadow( 0 0 2px rgba(0, 0, 0, 0.5))" }} />
			</DashboardMapaMarker>
			{props.sos && (
				<DashboardMapaMarker position={{ lat: props.sos.lat, lng: props.sos.lng }}>
					<Icon path={mdiAlphaHCircleOutline} size={1.5} className="text-air-blue bg-white rounded-circle" style={{ filter: "drop-shadow( 0 0 2px rgba(0, 0, 0, 0.5))" }} />
				</DashboardMapaMarker>
			)}
			{props.markers.map((marker) => {
				return (
					<DashboardMapaMarker
						key={marker.vooId}
						onRef={(item) => {
							setMarkers((prev) => [...prev, item]);
						}}
						onChange={(data: MissaoLogDetalhesModel) => {
							props.onVideosChange([...props.videos, data]);
						}}
						missaoLogId={marker.missaoLogId}
						position={marker}
					>
						<div className={`d-flex align-items-center justify-content-center text-white bg-air-blue rounded-circle shadow-sm`} style={{ width: 30, height: 30 }}>
							1
						</div>
					</DashboardMapaMarker>
				);
			})}
		</>
	);
}

type DashboardMapaMarkerProps = {
	onRef?: (ref: google.maps.marker.AdvancedMarkerElement) => void;
	onClick?: () => void;
	onChange?: (data: MissaoLogDetalhesModel) => void;
	missaoLogId?: number;
	position: { lat: number; lng: number };
	children: ReactNode | ReactNode[];
};

function DashboardMapaMarker(props: DashboardMapaMarkerProps) {
	const { handleLogout } = useAuth();
	const apiService = new ApiService(handleLogout);

	const [loading, setLoading] = useState(false);
	const [video, setVideo] = useState(false);
	const [data, setData] = useState<MissaoLogDetalhesModel>();

	const markerRef = useCallback((marker: google.maps.marker.AdvancedMarkerElement) => {
		if (marker && props.onRef) {
			props.onRef(marker);
		}
		return marker;
		// eslint-disable-next-line
	}, []);

	async function handleClick(missaoLogId: number) {
		setLoading(true);
		if (!data) {
			let resp = await apiService.GetMissaoLogDetalhe(missaoLogId);
			if (resp.Data) {
				setData({ ...resp.Data, missaoId: missaoLogId });
			}
		}
		setVideo(true);
		setLoading(false);
	}

	return (
		<AdvancedMarker
			position={props.position}
			ref={markerRef}
			onClick={() => {
				if (props.missaoLogId) {
					handleClick(props.missaoLogId);
				}
			}}
		>
			{loading && (
				<div className="position-absolute" style={{ zIndex: 2 }}>
					<Spinner />
				</div>
			)}
			{props.children}
			{video && data && (
				<DashboardMapaMarkerVideo
					data={data}
					onChange={() => {
						setVideo(false);
						if (props.onChange) {
							props.onChange(data);
						}
					}}
					onClose={() => {
						setVideo(false);
					}}
				/>
			)}
		</AdvancedMarker>
	);
}

type DashboardMapaMarkerVideoProps = {
	data: MissaoLogDetalhesModel;
	onChange: () => void;
	onClose: () => void;
};

function DashboardMapaMarkerVideo(props: DashboardMapaMarkerVideoProps) {
	const navigate = useNavigate();

	const videoJsOption = useMemo(() => {
		return {
			autoplay: true,
			playsinline: true,
			controls: true,
			responsive: true,
			liveui: true,
			fill: true,
			sources: props.data.parametros.videoURL,
			poster: process.env.PUBLIC_URL + "/logo_escuro.png",
		};
	}, [props.data]);

	function handleAlterar(e: any) {
		e.stopPropagation();
		props.onChange();
	}

	function handleLink(e: any) {
		e.stopPropagation();
		navigate(`/voo/${props.data.missaoId}`);
	}

	function handleClose(e: any) {
		e.stopPropagation();
		props.onClose();
	}

	return (
		<div
			className={`d-flex flex-column position-fixed rounded overflow-hidden shadow`}
			style={{ width: 300, height: 200, zIndex: 2, left: -130, top: -205, background: "rgba(31, 37, 60, 0.6)", padding: 2, backdropFilter: "blur(5px)" }}
		>
			<div className={`d-flex align-items-center gap-1 text-white p-1 fs-6`}>
				<small className="me-auto lh-1" style={{ fontFamily: "var(--bs-body-font-family)" }}>
					<div className="line-1">{props.data.piloto.nome}</div>
					<div className="line-1 w-light" style={{ fontSize: 10 }}>
						{props.data.drone.nome} - {props.data.data.dateString}
					</div>
				</small>
				<Button variant="light" className="rounded-circle p-0" onClick={handleAlterar} style={{ lineHeight: 0.7 }}>
					<Icon path={mdiPin} size={0.7} />
				</Button>
				<Button variant="light" className="rounded-circle p-0" onClick={handleLink} style={{ lineHeight: 0.7 }}>
					<Icon path={mdiChevronRight} size={0.7} className="text-air-dark" />
				</Button>
				<Button variant="light" className="rounded-circle p-0" onClick={handleClose} style={{ lineHeight: 0.7 }}>
					<Icon path={mdiClose} size={0.7} className="text-air-dark" />
				</Button>
			</div>
			<div className="flex-fill">
				<VideoJS options={videoJsOption} className=" bg-dark" style={{ width: "100%", height: "100%" }} />
			</div>
		</div>
	);
}

type DashboardMapaVideoProps = {
	index: number;
	width: number;
	data?: MissaoLogDetalhesModel;
	missaoLogId?: number;
	template: "grid" | "absolute";
	onClose: () => void;
};

function DashboardMapaVideo(props: DashboardMapaVideoProps) {
	const navigate = useNavigate();
	const { handleLogout } = useAuth();
	const apiService = new ApiService(handleLogout);

	const [position, setPosition] = useState({ x: 50 * props.index, y: 50 * props.index });
	const [data, setData] = useState(props.data);

	useEffect(() => {
		if (props.data === undefined && props.missaoLogId) {
			handleData(props.missaoLogId);
		}
	}, [props.data, props.missaoLogId]);

	const videoJsOption = useMemo(() => {
		return {
			autoplay: true,
			playsinline: true,
			controls: true,
			responsive: true,
			liveui: true,
			fill: true,
			sources: data?.parametros?.videoURL,
			poster: process.env.PUBLIC_URL + "/logo_escuro.png",
		};
	}, [data?.parametros?.videoURL]);

	async function handleData(missaoLogId: number) {
		let resp = await apiService.GetMissaoLogDetalhe(missaoLogId);
		if (resp.Data) {
			setData({ ...resp.Data!, missaoId: missaoLogId });
		}
	}

	function handleLink(e: any) {
		e.stopPropagation();
		navigate(`/voo/${data?.missaoId}`);
	}

	function handleClose(e: any) {
		e.stopPropagation();
		props.onClose();
	}

	return (
		<>
			{props.template === "absolute" ? (
				<Draggable
					handle=".handle"
					position={position}
					onDrag={(_, position) => {
						setPosition(position);
					}}
					bounds="parent"
					scale={1}
				>
					<Resizable
						defaultSize={{
							width: 300,
							height: 200,
						}}
						minWidth={150}
						minHeight={100}
						className={`d-flex flex-column position-absolute rounded overflow-hidden shadow`}
						style={{ zIndex: 2, background: "rgba(31, 37, 60, 0.6)", padding: 2, backdropFilter: "blur(5px)", pointerEvents: "all" }}
					>
						<div className={`d-flex align-items-center gap-1 text-white p-1 fs-6 handle`} style={{ cursor: "move" }}>
							<small className="me-auto lh-1" style={{ fontFamily: "var(--bs-body-font-family)" }}>
								<div className="line-1">{data?.piloto?.nome}</div>
								<div className="line-1 w-light" style={{ fontSize: 10 }}>
									{data?.drone?.nome} - {data?.data?.dateString}
								</div>
							</small>
							<Button variant="light" className="rounded-circle p-0" onClick={handleLink} style={{ lineHeight: 0.7 }}>
								<Icon path={mdiChevronRight} size={0.7} className="text-air-dark" />
							</Button>
							<Button variant="light" className="rounded-circle p-0" onClick={handleClose} style={{ lineHeight: 0.7 }}>
								<Icon path={mdiClose} size={0.7} className="text-air-dark" />
							</Button>
						</div>
						{!data && (
							<div className="d-flex text-center" style={{ width: "100%", height: "100%", placeItems: "center" }}>
								<Spinner className="text-white mx-auto" />
							</div>
						)}
						{data && <VideoJS options={videoJsOption} className="bg-dark" style={{ width: "100%", height: "100%" }} />}
					</Resizable>
				</Draggable>
			) : (
				<div
					className={`d-flex flex-column  rounded overflow-hidden shadow`}
					style={{ width: "100%", aspectRatio: 1.5, zIndex: 2, background: "rgba(31, 37, 60, 0.6)", padding: 2, backdropFilter: "blur(5px)", pointerEvents: "all" }}
				>
					<div className={`d-flex align-items-center gap-1 text-white p-1 fs-6 handle`}>
						<small className="me-auto lh-1" style={{ fontFamily: "var(--bs-body-font-family)" }}>
							<div className="line-1">{data?.piloto?.nome}</div>
							<div className="line-1 w-light" style={{ fontSize: 10 }}>
								{data?.drone?.nome} - {data?.data?.dateString}
							</div>
						</small>
						<Button variant="light" className="rounded-circle p-0" onClick={handleLink} style={{ lineHeight: 0.7 }}>
							<Icon path={mdiChevronRight} size={0.7} className="text-air-dark" />
						</Button>
						<Button variant="light" className="rounded-circle p-0" onClick={handleClose} style={{ lineHeight: 0.7 }}>
							<Icon path={mdiClose} size={0.7} className="text-air-dark" />
						</Button>
					</div>
					{!data && (
						<div className="d-flex text-center" style={{ width: "100%", height: "100%", placeItems: "center" }}>
							<Spinner className="text-white mx-auto" />
						</div>
					)}
					{data && <VideoJS options={videoJsOption} className="bg-dark" style={{ width: "100%", height: "100%" }} />}
				</div>
			)}
		</>
	);
}
